/**
 * Nested client address payload as returned by the Housien API.
 */
export type ClientAddressPayload = {
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zip_code?: string;
};

/**
 * Formats a nested API client address into a single display line.
 * Canonical storage shape is the nested Address VO — this is presentation only.
 *
 * @param address - Nested address from the API, or nullish.
 * @returns A human-readable address string, or empty when absent/invalid.
 */
export function formatClientAddress(address: unknown): string {
  if (address == null || typeof address !== 'object') return '';
  const a = address as ClientAddressPayload;
  const streetLine = [asText(a.street), asText(a.number)].filter(Boolean).join(', ');
  const cityLine = [asText(a.city), asText(a.state)].filter(Boolean).join('/');
  return [streetLine, asText(a.complement), asText(a.neighborhood), cityLine, asText(a.zip_code)]
    .filter(Boolean)
    .join(' - ');
}

/**
 * Coerces a value to a trimmed string when it is a non-empty string.
 *
 * @param value - Candidate field value.
 * @returns Trimmed text, or empty string.
 */
function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}
